import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Plus, Trash2, ChevronDown, ChevronUp, ClipboardList, Eye } from 'lucide-react';

const DEFAULT_AREAS = ['General Intelligence', 'Academics', 'Communication'];
const ADDITIONAL_AREAS = ['Adaptive Behavior', 'Social/Emotional', 'Motor Skills', 'Behavioral', 'Other'];

function mergeContent(existing) {
  const defaults = {
    assessment_areas: [
      { area_id: 'general_intelligence', area_name: 'General Intelligence', assessments: [] },
      { area_id: 'academics', area_name: 'Academics', assessments: [] },
      { area_id: 'communication', area_name: 'Communication', assessments: [] },
    ],
  };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.assessment_areas) return defaults;
  const { validity_statement, ...rest } = existing;
  return { ...defaults, ...rest, classroom_observations: existing.classroom_observations || [] };
}

export default function EvaluationAssessmentsEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const addArea = (areaName) => {
    setContent(prev => ({
      ...prev, assessment_areas: [...prev.assessment_areas, {
        area_id: `area_${Date.now()}`, area_name: areaName, assessments: [],
      }],
    }));
  };
  const removeArea = (idx) => setContent(prev => ({ ...prev, assessment_areas: prev.assessment_areas.filter((_, i) => i !== idx) }));

  const addAssessment = (areaIdx) => {
    setContent(prev => ({
      ...prev, assessment_areas: prev.assessment_areas.map((area, i) =>
        i === areaIdx ? { ...area, assessments: [...area.assessments, {
          id: `asmt_${Date.now()}`, assessment_name: '', scores: '', interpretation: '', evaluator: '',
        }]} : area
      ),
    }));
  };
  const removeAssessment = (areaIdx, asmtIdx) => {
    setContent(prev => ({
      ...prev, assessment_areas: prev.assessment_areas.map((area, i) =>
        i === areaIdx ? { ...area, assessments: area.assessments.filter((_, j) => j !== asmtIdx) } : area
      ),
    }));
  };
  const updateAssessment = (areaIdx, asmtIdx, field, value) => {
    setContent(prev => ({
      ...prev, assessment_areas: prev.assessment_areas.map((area, i) =>
        i === areaIdx ? { ...area, assessments: area.assessments.map((a, j) =>
          j === asmtIdx ? { ...a, [field]: value } : a
        )} : area
      ),
    }));
  };
  const handleSave = () => onSave(content);

  const existingNames = new Set(content.assessment_areas.map(a => a.area_name));
  const availableAreas = ADDITIONAL_AREAS.filter(a => !existingNames.has(a));
  const totalAssessments = content.assessment_areas.reduce((sum, a) => sum + a.assessments.length, 0);

  return (
    <div className="max-w-4xl space-y-6" data-testid="eval-assessments-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="eval-assessments-title">Evaluation Assessments</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <p className="text-xs text-[#5A8A5A] mt-2">{content.assessment_areas.length} areas, {totalAssessments} assessments</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="eval-assessments-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Add Assessment Area */}
      {availableAreas.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-[#595959]">Add area:</span>
          {availableAreas.map(area => (
            <Button key={area} variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={() => addArea(area)} data-testid={`add-area-${area.replace(/\//g, '-').toLowerCase()}`}>
              <Plus className="h-3 w-3 mr-1" />{area}
            </Button>
          ))}
        </div>
      )}

      {/* Assessment Areas */}
      {content.assessment_areas.map((area, areaIdx) => {
        const isCollapsed = collapsed[area.area_id];
        return (
          <div key={area.area_id} className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid={`assessment-area-${areaIdx}`}>
            <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1] cursor-pointer" onClick={() => setCollapsed(prev => ({ ...prev, [area.area_id]: !prev[area.area_id] }))}>
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-[#5A8A5A]" />
                <h3 className="font-medium text-sm text-[#1C1C1C]">{area.area_name}</h3>
                <span className="text-xs text-[#595959]">({area.assessments.length} assessments)</span>
              </div>
              <div className="flex items-center gap-2">
                {!DEFAULT_AREAS.includes(area.area_name) && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={e => { e.stopPropagation(); removeArea(areaIdx); }}>
                    <Trash2 className="h-3 w-3 mr-1" />Remove
                  </Button>
                )}
                {isCollapsed ? <ChevronDown className="h-4 w-4 text-[#595959]" /> : <ChevronUp className="h-4 w-4 text-[#595959]" />}
              </div>
            </div>
            {!isCollapsed && (
              <div className="p-5">
                {area.assessments.length === 0 ? (
                  <p className="text-sm text-[#595959] text-center py-3">No assessments in this area yet.</p>
                ) : (
                  <div className="space-y-4">
                    {area.assessments.map((asmt, asmtIdx) => (
                      <div key={asmt.id || asmtIdx} className="border border-[#E5E5E5] rounded-xl p-4 relative group" data-testid={`assessment-${areaIdx}-${asmtIdx}`}>
                        <button onClick={() => removeAssessment(areaIdx, asmtIdx)} className="absolute top-3 right-3 p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition">
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </button>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Assessment Name</label>
                            <Input value={asmt.assessment_name} onChange={e => updateAssessment(areaIdx, asmtIdx, 'assessment_name', e.target.value)}
                              placeholder="e.g., WISC-V, WJ-IV, CELF-5" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Scores / Results</label>
                            <Input value={asmt.scores} onChange={e => updateAssessment(areaIdx, asmtIdx, 'scores', e.target.value)}
                              placeholder="e.g., FSIQ: 95, VCI: 102" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Interpretation</label>
                          <Textarea value={asmt.interpretation} onChange={e => updateAssessment(areaIdx, asmtIdx, 'interpretation', e.target.value)}
                            placeholder="Interpretation of results..." rows={3} className="rounded-xl border-[#E5E5E5] text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Evaluator</label>
                          <Input value={asmt.evaluator} onChange={e => updateAssessment(areaIdx, asmtIdx, 'evaluator', e.target.value)}
                            placeholder="Name / credentials" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="outline" size="sm" className="rounded-lg text-xs mt-3" onClick={() => addAssessment(areaIdx)} data-testid={`add-assessment-${areaIdx}`}>
                  <Plus className="h-3 w-3 mr-1" />Add Assessment
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {/* Classroom Observations (multi-entry) */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid="classroom-observations-section">
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1]">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-[#5A8A5A]" />
            <h3 className="font-medium text-sm text-[#1C1C1C]">Classroom Observations ({(content.classroom_observations || []).length})</h3>
          </div>
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7"
            onClick={() => setContent(prev => ({ ...prev, classroom_observations: [...(prev.classroom_observations || []), { id: `obs_${Date.now()}`, date: '', observer: '', setting: '', observation: '' }] }))}
            data-testid="add-classroom-observation">
            <Plus className="h-3 w-3 mr-1" />Add Observation
          </Button>
        </div>
        <div className="p-5">
          {(content.classroom_observations || []).length === 0 ? (
            <p className="text-sm text-[#595959] text-center py-4">No classroom observations added yet.</p>
          ) : (
            <div className="space-y-4">
              {content.classroom_observations.map((obs, idx) => (
                <div key={obs.id || idx} className="border border-[#E5E5E5] rounded-xl p-4 relative group" data-testid={`classroom-obs-${idx}`}>
                  <button onClick={() => setContent(prev => ({ ...prev, classroom_observations: prev.classroom_observations.filter((_, i) => i !== idx) }))}
                    className="absolute top-3 right-3 p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition">
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Date</label>
                      <Input type="date" value={obs.date || ''} onChange={e => {
                        const updated = content.classroom_observations.map((o, i) => i === idx ? { ...o, date: e.target.value } : o);
                        setContent(prev => ({ ...prev, classroom_observations: updated }));
                      }} className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Observer</label>
                      <Input value={obs.observer || ''} onChange={e => {
                        const updated = content.classroom_observations.map((o, i) => i === idx ? { ...o, observer: e.target.value } : o);
                        setContent(prev => ({ ...prev, classroom_observations: updated }));
                      }} placeholder="Observer name" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Setting</label>
                      <Input value={obs.setting || ''} onChange={e => {
                        const updated = content.classroom_observations.map((o, i) => i === idx ? { ...o, setting: e.target.value } : o);
                        setContent(prev => ({ ...prev, classroom_observations: updated }));
                      }} placeholder="e.g., General ed classroom — literacy block" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Observation Notes</label>
                    <Textarea value={obs.observation || ''} onChange={e => {
                      const updated = content.classroom_observations.map((o, i) => i === idx ? { ...o, observation: e.target.value } : o);
                      setContent(prev => ({ ...prev, classroom_observations: updated }));
                    }} placeholder="Describe classroom observation..." rows={4} className="rounded-xl border-[#E5E5E5] text-sm" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="eval-assessments-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Evaluation Assessments'}
        </Button>
      </div>
    </div>
  );
}
